import defaultSalesData from "../data/salesData.json";

/**
 * Fetch sales data from API or fallback to dummy data
 * @param {string} date - Optional date parameter for the report
 * @returns {Promise<Object>} The sales data
 */
export const fetchSalesData = async (date = null) => {
  try {
    // API implementation will go here when you're ready to connect to your backend
    // Example:
    // const response = await fetch(`/api/sales-report${date ? `?date=${date}` : ''}`);
    // if (!response.ok) throw new Error('Failed to fetch sales data');
    // return await response.json();

    // For now, return the dummy data with a slight delay to simulate API call
    await new Promise((resolve) => setTimeout(resolve, 500));
    return defaultSalesData;
  } catch (error) {
    console.error("Error fetching sales data:", error);
    // Fallback to dummy data in case of error
    return defaultSalesData;
  }
};

/**
 * Format currency values according to locale
 * @param {number} value - The number to format
 * @param {string} currency - Currency symbol
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (value, currency = "₦") => {
  return `${currency} ${value.toLocaleString()}`;
};

/**
 * Calculate totals from sales items
 * @param {Array} items - Sales items array
 * @returns {Object} Calculated totals
 */
export const calculateTotals = (items) => {
  if (!items || !items.length) return { total: 0, itemCount: 0 };

  return {
    total: items.reduce((sum, item) => sum + item.total, 0),
    itemCount: items.length,
    itemQuantity: items.reduce((sum, item) => sum + item.qty, 0),
  };
};

/**
 * Process raw sales data to enrich it with calculated values
 * @param {Object} rawData - Raw sales data from API or JSON
 * @returns {Object} Processed data
 */
export const processSalesData = (rawData) => {
  if (!rawData) return null;

  const processedData = { ...rawData };

  // Ensure income total is calculated if not provided
  if (processedData.income && !processedData.income.total) {
    processedData.income.total =
      (processedData.income.fromProducts || 0) +
      (processedData.income.fromClearedLoan || 0) -
      (processedData.income.expenses || 0);
  }

  // Add calculated totals
  processedData.calculatedTotals = calculateTotals(processedData.items);

  return processedData;
};

/**
 * Get sales report data
 * @param {string} date - Optional date for report
 * @returns {Promise<Object>} Processed sales data
 */
export const getSalesReport = async (date = null) => {
  const rawData = await fetchSalesData(date);
  return processSalesData(rawData);
};
